import { z } from 'zod';
import type { BlockItem, DashboardConfig, WidgetConfig } from '../types/dashboard-config.js';

/** Flattens every widget in a DashboardConfig, including those nested inside sections. */
export function collectAllWidgets(config: DashboardConfig): WidgetConfig[] {
  const widgets: WidgetConfig[] = [];
  for (const block of config.blocks) {
    collectWidgetsFromBlock(block.items, widgets);
  }
  return widgets;
}

function collectWidgetsFromBlock(items: BlockItem[], widgets: WidgetConfig[]): void {
  for (const item of items) {
    if (item.type === 'widget') {
      widgets.push(item.widget);
    } else {
      for (const bw of item.section.widgets) {
        widgets.push(bw.widget);
      }
    }
  }
}

// ─── Widget Snapshot ──────────────────────────────────────────────────────────
//
// The agent-facing contract for chat-api's page-snapshot tool: compact projections of a widget's
// resolved CanonicalWidgetData. The projections themselves live in backend-core
// (`services/widget/registry.ts` `projectWidgetSnapshot`, `services/widget/snapshot.ts`).

const ChartSeriesPointSchema = z.object({ x: z.union([z.string(), z.number()]), y: z.number() });
const ChartSeriesSchema = z.object({
  label: z.string().optional(),
  points: z.array(ChartSeriesPointSchema),
});
const StatGridItemSchema = z.object({
  label: z.string(),
  value: z.string(),
  unit: z.string().optional(),
});

export const WidgetSnapshotEntrySchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('series_chart'),
    widgetId: z.string(),
    title: z.string(),
    xAxisTitle: z.string().optional(),
    yAxisTitle: z.string().optional(),
    series: z.array(ChartSeriesSchema),
  }),
  z.object({
    kind: z.literal('data_table'),
    widgetId: z.string(),
    title: z.string(),
    columns: z.array(z.string()),
    /** Row labels for `previewRows`, in the same order — capped to the same row count. */
    rowLabels: z.array(z.string()),
    rowCount: z.number().int(),
    previewRows: z.array(z.array(z.string().nullable())),
  }),
  z.object({
    kind: z.literal('stat_grid'),
    widgetId: z.string(),
    title: z.string(),
    items: z.array(StatGridItemSchema),
  }),
  z.object({
    kind: z.literal('text'),
    widgetId: z.string(),
    title: z.string(),
    content: z.string(),
  }),
  z.object({
    kind: z.literal('quote'),
    widgetId: z.string(),
    title: z.string(),
    speaker: z.string().optional(),
    content: z.string(),
  }),
  z.object({
    kind: z.literal('unsupported_widget_type'),
    widgetId: z.string(),
    title: z.string(),
    widgetType: z.string(),
  }),
  z.object({
    kind: z.literal('no_data'),
    widgetId: z.string(),
    title: z.string(),
  }),
]);
export type WidgetSnapshotEntry = z.infer<typeof WidgetSnapshotEntrySchema>;

// NOTE: `pageId` is required and numeric because Page-backed dashboards are the only supported source today.
// Legacy segment dashboards are keyed by `segmentIdentifier` (a string) and have no page ID, so when the legacy
// provider is reintroduced (see PageSnapshotRef in chat-api's pages/snapshot.provider.base.ts) this schema must
// change with it — e.g. make `pageId` optional or replace it with a generic ref/id. That change is deliberately
// deferred: this is the agent-facing contract, so it should be shaped against the real legacy provider, not guessed.
export const DashboardPageSnapshotSchema = z.object({
  pageId: z.number().int(),
  pageName: z.string(),
  widgets: z.array(WidgetSnapshotEntrySchema),
  /** Set when widgets were dropped to stay under the serialized size budget. */
  truncated: z.boolean().optional(),
  omittedWidgetCount: z.number().int().optional(),
});
export type DashboardPageSnapshot = z.infer<typeof DashboardPageSnapshotSchema>;
